import { c as _c } from "react/compiler-runtime";
import { COUNTRIES } from '@onefootprint/core';
import type { Iso3166TwoDigitCountryCode, Locale } from '@onefootprint/request-types';
import { useId } from 'react';
import { Controller, type ControllerProps, type FieldValues, type Path, type PathValue, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import BaseSelect, { type BaseSelectOption } from '../../internal/base-select';
import BaseSelectTrigger from '../../internal/base-select-trigger';
import FormWrapper from '../internals/form-wrapper';
import { getCountriesWithCallingCode } from '../utils/tel-utils';
import type { FormInputProps } from './form-input';
export type CountrySelectOption = {
  label: string;
  value: Iso3166TwoDigitCountryCode;
};
export type FormCountryProps<TFieldValues extends FieldValues> = {
  name: Path<TFieldValues>;
  rules?: ControllerProps<TFieldValues>['rules'];
  onChange?: (value: Iso3166TwoDigitCountryCode) => void;
  onBlur?: () => void;
  locale?: Locale;
  defaultValue?: Iso3166TwoDigitCountryCode;
  /** Accepted for API compatibility; the react-hook-form field value is the source of truth. */
  value?: Iso3166TwoDigitCountryCode;
  options?: CountrySelectOption[];
  showCallingCode?: boolean;
  searchPlaceholder?: string;
  emptyStateText?: string;
} & Omit<FormInputProps<TFieldValues>, 'name' | 'rules' | 'type' | 'autoComplete' | 'onChange' | 'onBlur' | 'defaultValue' | 'value' | 'mask'>;
export default function FormCountry(t0) {
  const $ = _c(29);
  const {
    rules,
    name,
    label,
    hint,
    addon,
    errorPosition: t1,
    orientation: t2,
    size: t3,
    id: baseId,
    onChange,
    onBlur,
    defaultValue,
    disabled,
    showCallingCode: t4,
    options: t5,
    searchPlaceholder,
    emptyStateText,
    placeholder,
    className,
    "aria-label": ariaLabel
  } = t0;
  const errorPosition = t1 === undefined ? "relative" : t1;
  const orientation = t2 === undefined ? "vertical" : t2;
  const size = t3 === undefined ? "default" : t3;
  const showCallingCode = t4 === undefined ? false : t4;
  const options = t5 === undefined ? COUNTRIES : t5;
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      keyPrefix: "components.country-select"
    };
    $[0] = t6;
  } else {
    t6 = $[0];
  }
  const {
    t
  } = useTranslation("ui", t6);
  const {
    control
  } = useFormContext();
  const contextId = useId();
  const id = baseId || contextId;
  let t7;
  if ($[1] !== options || $[2] !== showCallingCode) {
    t7 = showCallingCode ? getCountriesWithCallingCode(options) : options;
    $[1] = options;
    $[2] = showCallingCode;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const countriesOptions = t7 as BaseSelectOption<Iso3166TwoDigitCountryCode>[];
  const selectSize = size === "compact" ? "compact" : "default";
  const t8 = defaultValue as PathValue<TFieldValues, Path<TFieldValues>>;
  let t9;
  if ($[4] !== addon || $[5] !== ariaLabel || $[6] !== className || $[7] !== countriesOptions || $[8] !== disabled || $[9] !== emptyStateText || $[10] !== errorPosition || $[11] !== hint || $[12] !== id || $[13] !== label || $[14] !== onBlur || $[15] !== onChange || $[16] !== orientation || $[17] !== placeholder || $[18] !== searchPlaceholder || $[19] !== selectSize || $[20] !== size || $[21] !== t) {
    t9 = t10 => {
      const {
        field,
        fieldState
      } = t10;
      const error = fieldState.error?.message;
      const isDisabled = disabled || field.disabled;
      const selectedOption = countriesOptions.find(option => option.value === field.value);
      const resolvedPlaceholder = placeholder || t("placeholder-default") as string;
      return <FormWrapper id={id} error={error} label={label} errorPosition={errorPosition} size={size} hint={hint} addon={addon} orientation={orientation} fullWidth={true} disabled={isDisabled}><BaseSelect id={id} options={countriesOptions} value={selectedOption} hasError={!!error} disabled={isDisabled} size={selectSize} searchPlaceholder={searchPlaceholder} emptyStateText={emptyStateText} onChange={option_0 => {
          field.onChange(option_0.value as PathValue<TFieldValues, Path<TFieldValues>>);
          onChange?.(option_0.value);
        }} onBlur={() => {
          field.onBlur();
          onBlur?.();
        }} renderTrigger={t11 => {
          const {
            isOpen,
            onClick,
            testID
          } = t11;
          return <BaseSelectTrigger id={id} testID={testID} className={className} onClick={onClick} disabled={isDisabled} hasError={!!error} hasFocus={isOpen} isEmpty={!selectedOption} size={size} aria-label={label ? undefined : ariaLabel || resolvedPlaceholder}>{selectedOption ? selectedOption.label : resolvedPlaceholder}</BaseSelectTrigger>;
        }} /></FormWrapper>;
    };
    $[4] = addon;
    $[5] = ariaLabel;
    $[6] = className;
    $[7] = countriesOptions;
    $[8] = disabled;
    $[9] = emptyStateText;
    $[10] = errorPosition;
    $[11] = hint;
    $[12] = id;
    $[13] = label;
    $[14] = onBlur;
    $[15] = onChange;
    $[16] = orientation;
    $[17] = placeholder;
    $[18] = searchPlaceholder;
    $[19] = selectSize;
    $[20] = size;
    $[21] = t;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== control || $[24] !== name || $[25] !== rules || $[26] !== t8 || $[27] !== t9) {
    t10 = <Controller control={control} name={name} rules={rules} defaultValue={t8} render={t9} />;
    $[23] = control;
    $[24] = name;
    $[25] = rules;
    $[26] = t8;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  return t10;
}